/**
 * The queue of local changes that have not reached the server.
 *
 * One row per record, keyed by resource and id, so repeated edits to the same game
 * collapse instead of piling up. Order within a resource is irrelevant — each entry
 * uploads the whole aggregate — but order *between* resources is not, which is what
 * {@link RESOURCES} fixes: a practice session is filed against a game, so the game
 * has to land first.
 */

import { OutboxEntry, OutboxOp, OutboxStatus, RESOURCES, Resource, db } from './db';

const keyOf = (resource: Resource, id: string): string => `${resource}:${id}`;

/**
 * Records that `id` needs uploading, replacing any earlier entry for it.
 *
 * A delete always supersedes a queued upsert, including for a record that only ever
 * existed on this device; the drain treats a 404 from a delete as success rather
 * than tracking whether the record was ever pushed.
 */
export async function enqueue(
  resource: Resource,
  id: string,
  op: OutboxOp,
  accountId?: string
): Promise<void> {
  const database = await db();
  const key = keyOf(resource, id);
  const now = Date.now();
  const existing = await database.get('outbox', key);

  await database.put('outbox', {
    key,
    resource,
    id,
    op,
    accountId: accountId ?? existing?.accountId,
    queuedAt: existing?.queuedAt ?? now,
    updatedAt: now,
    revision: (existing?.revision ?? 0) + 1,
    // A fresh edit deserves a fresh run at the server, so reset the failure state.
    attempts: 0,
    status: 'pending',
  });
}

export async function remove(resource: Resource, id: string): Promise<void> {
  await (await db()).delete('outbox', keyOf(resource, id));
}

/**
 * Clears an entry after a successful upload, unless it was written again while that
 * upload was in flight. The later write keeps its place in the queue.
 */
export async function clearIfUnchanged(entry: OutboxEntry): Promise<void> {
  const database = await db();
  const tx = database.transaction('outbox', 'readwrite');
  const current = await tx.store.get(entry.key);
  if (current && current.revision === entry.revision) {
    await tx.store.delete(entry.key);
  }
  await tx.done;
}

/** Every entry, in the order they must be uploaded. */
export async function all(): Promise<OutboxEntry[]> {
  const entries = await (await db()).getAll('outbox');
  return entries.sort(
    (a, b) => RESOURCES.indexOf(a.resource) - RESOURCES.indexOf(b.resource)
  );
}

/** Entries the next drain should attempt. Excludes anything blocked. */
export async function drainable(): Promise<OutboxEntry[]> {
  return (await all()).filter(entry => entry.status === 'pending');
}

/**
 * Keys of every record with an outstanding change, for the pull to skip.
 *
 * This is the invariant that keeps offline edits safe: a refresh from the server may
 * not touch a record the device has not finished uploading, whether that change is
 * pending or blocked.
 */
export async function heldKeys(): Promise<Set<string>> {
  return new Set((await all()).map(entry => entry.key));
}

export async function counts(): Promise<{ pending: number; blocked: number }> {
  const entries = await all();
  return {
    pending: entries.filter(e => e.status === 'pending').length,
    blocked: entries.filter(e => e.status === 'blocked').length,
  };
}

/**
 * Records the outcome of a failed upload.
 *
 * Skipped if the record was written again while the upload was in flight: that newer
 * entry is pending and deserves its own attempt, so neither the failure count nor a
 * `blocked` status should carry over to it.
 */
async function markOutcome(
  entry: OutboxEntry,
  status: OutboxStatus,
  error: string
): Promise<void> {
  const database = await db();
  const tx = database.transaction('outbox', 'readwrite');
  const current = await tx.store.get(entry.key);
  if (current && current.revision === entry.revision) {
    await tx.store.put({
      ...current,
      attempts: current.attempts + 1,
      status,
      lastError: error,
    });
  }
  await tx.done;
}

/** A transient failure: keep retrying. */
export const markFailed = (entry: OutboxEntry, error: string): Promise<void> =>
  markOutcome(entry, 'pending', error);

/** A refusal retrying will not fix. Holds the data and waits for the user. */
export const markBlocked = (entry: OutboxEntry, error: string): Promise<void> =>
  markOutcome(entry, 'blocked', error);

/** Puts blocked entries back in the queue, for after credits are topped up. */
export async function unblockAll(): Promise<number> {
  const database = await db();
  const blocked = (await database.getAll('outbox')).filter(e => e.status === 'blocked');

  const tx = database.transaction('outbox', 'readwrite');
  await Promise.all(
    blocked.map(entry =>
      tx.store.put({ ...entry, status: 'pending' as const, attempts: 0 })
    )
  );
  await tx.done;

  return blocked.length;
}
